import React, { useState, useEffect } from 'react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import {
    Users, CheckCircle2, XCircle, Clock,
    Calendar, AlertCircle, PieChart as PieChartIcon, BarChart2, MoreVertical,
    ChevronDown, ChevronUp, LayoutDashboard
} from 'lucide-react';
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
    CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer
} from 'recharts';
import CustomDatePicker from '../../../components/Common/DatePicker/CustomDatePicker';
import Pagination from '../../../components/Common/Pagination/Pagination';
import NoData from '../../../components/Common/NoData/NoData';
import { useNavigate } from 'react-router-dom';
import styles from './HRDashboard.module.css';

const HRDashboard = () => {
    const navigate = useNavigate();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [dashboardData, setDashboardData] = useState(null);
    const [isSummaryOpen, setIsSummaryOpen] = useState(false);

    // Employee List View State
    const [employeesList, setEmployeesList] = useState([]);
    const [isEmpLoading, setIsEmpLoading] = useState(false);
    const [empPagination, setEmpPagination] = useState({
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10
    });
    const [openMenuIndex, setOpenMenuIndex] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

    const toggleActionMenu = (id, e) => {
        if (openMenuIndex === id) {
            setOpenMenuIndex(null);
        } else {
            const rect = e.currentTarget.getBoundingClientRect();
            setMenuPosition({ top: rect.bottom + 4, left: rect.right - 160 });
            setOpenMenuIndex(id);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.action-wrapper')) {
                setOpenMenuIndex(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const fetchEmployeesList = async () => {
        try {
            setIsEmpLoading(true);
            const response = await api.get('/visit-sessions', {
                params: {
                    page: empPagination.current_page,
                    per_page: empPagination.per_page,
                    date: date
                }
            });
            if (response.data) {
                const resData = response.data;
                if (resData.success && resData.data) {
                    setEmployeesList(resData.data.data || []);
                    setEmpPagination(prev => ({
                        ...prev,
                        current_page: resData.data.current_page || prev.current_page,
                        last_page: resData.data.last_page || 1,
                        total: resData.data.total || 0,
                        per_page: resData.data.per_page || prev.per_page
                    }));
                } else {
                    setEmployeesList(resData.data || []);
                    setEmpPagination(prev => ({
                        ...prev,
                        current_page: resData.current_page || prev.current_page,
                        last_page: resData.last_page || 1,
                        total: resData.total || 0,
                        per_page: resData.per_page || prev.per_page
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching visit sessions:', error);
        } finally {
            setIsEmpLoading(false);
        }
    };

    const getInitials = (name) => {
        if (!name) return '';
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const getAvatarColor = (name) => {
        if (!name) return '#f1f5f9';
        const colors = ['#fee2e2', '#fef3c7', '#dcfce7', '#d1fae5', '#e0f2fe', '#f3e8ff', '#fce7f3'];
        const charCodeSum = name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
        return colors[charCodeSum % colors.length];
    };

    const getAvatarTextColor = (bgColor) => {
        if (bgColor === '#f1f5f9') return '#64748b';
        return bgColor.replace('f', '9').replace('e', '7').replace('d', '5');
    };

    const fetchDashboard = async () => {
        try {
            setLoading(true);
            const response = await api.get('/attendance/dashboard', {
                params: { date }
            });
            if (response.data.success) {
                setDashboardData(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching HR dashboard:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (newDate) => {
        if (newDate) {
            setDate(newDate.toLocaleDateString('en-CA')); // YYYY-MM-DD
        } else {
            setDate('');
        }
    };

    useEffect(() => {
        fetchDashboard();
    }, [date]);

    useEffect(() => {
        fetchEmployeesList();
    }, [empPagination.current_page, empPagination.per_page, date]);

    const COLORS = {
        present: '#10b981', // emerald-500
        absent: '#ef4444',  // red-500
        leave: '#a855f7',   // purple-500
        holiday: '#3b82f6', // blue-500
        inProgress: '#f59e0b' // amber-500
    };

    const getPieData = () => {
        if (!dashboardData) return [];
        const totalPresent = dashboardData.full_day + dashboardData.half_day + dashboardData.first_half + dashboardData.second_half;
        return [
            { name: 'Present', value: totalPresent, color: COLORS.present },
            { name: 'Absent', value: dashboardData.absent, color: COLORS.absent },
            { name: 'On Leave', value: dashboardData.leave + dashboardData.leave_working, color: COLORS.leave },
            { name: 'Holiday', value: dashboardData.holiday + dashboardData.holiday_working, color: COLORS.holiday },
            { name: 'In Progress', value: dashboardData.in_progress, color: COLORS.inProgress }
        ].filter(item => item.value > 0);
    };

    const getBarData = () => {
        if (!dashboardData) return [];
        return [
            { name: 'Full Day', value: dashboardData.full_day },
            { name: 'Half Day', value: dashboardData.half_day + dashboardData.first_half + dashboardData.second_half },
            { name: 'Absent', value: dashboardData.absent },
            { name: 'Leave', value: dashboardData.leave + dashboardData.leave_working },
            { name: 'Pending', value: dashboardData.pending_admin_approval }
        ];
    };

    const formatTime = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    };

    const formatDuration = (minutes) => {
        if (!minutes) return '-';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins}m`;
    };

    const getStatusClass = (status) => {
        if (!status) return styles['status-pending'];
        const s = status.toLowerCase();
        if (s.includes('full_day') || s.includes('working')) return styles['status-present'];
        if (s.includes('absent')) return styles['status-absent'];
        if (s.includes('half')) return styles['status-half'];
        if (s.includes('leave')) return styles['status-leave'];
        return styles['status-pending'];
    };

    return (
        <div className={styles['dashboard-layout']}>
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
            {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

            <div className={styles['content-wrapper']}>
                <Header onMenuClick={toggleSidebar} />

                <main className={styles['main-content']}>
                    <Breadcrumbs customItems={[
                        { label: 'HR', path: '#' },
                        { label: 'HR Dashboard', path: '/hr/dashboard' }
                    ]} />

                    <div className={styles['page-header']}>
                        <div className={styles['header-title-section']}>
                            <h1>HR Dashboard</h1>
                            <p>Company-wide attendance and workforce metrics overview.</p>
                        </div>
                        <div className={styles['dashboard-date-wrapper']}>
                            <CustomDatePicker
                                selected={date}
                                onChange={handleDateChange}
                                placeholderText="Select Date"
                                popperPlacement="bottom-end"
                            />
                        </div>
                    </div>

                    {!loading && dashboardData && (
                        <>
                            {/* Key Metrics Grid */}
                            <div className={styles['summary-collapsible']}>
                                <button
                                    className={styles['summary-toggle']}
                                    onClick={() => setIsSummaryOpen(prev => !prev)}
                                >
                                    <div className={styles['summary-toggle-left']}>
                                        <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                                        <div className={styles['summary-toggle-text']}>
                                            <span className={styles['summary-toggle-title']}> Dashboard Summary</span>
                                            <span className={styles['summary-toggle-hint']}>
                                                {dashboardData.total_employees} Employees · {dashboardData.full_day + dashboardData.half_day + dashboardData.first_half + dashboardData.second_half} Present
                                            </span>
                                        </div>
                                    </div>
                                    {isSummaryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                </button>

                                <div className={`${styles['metrics-grid']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
                                    <div className={styles['metric-card']}>
                                    <div className={`${styles['metric-icon']} ${styles.primary}`}>
                                        <Users size={24} />
                                    </div>
                                    <div className={styles['metric-info']}>
                                        <span className={styles['metric-value']}>{dashboardData.total_employees}</span>
                                        <span className={styles['metric-label']}>Total Employees</span>
                                    </div>
                                </div>
                                <div className={styles['metric-card']}>
                                    <div className={`${styles['metric-icon']} ${styles.success}`}>
                                        <CheckCircle2 size={24} />
                                    </div>
                                    <div className={styles['metric-info']}>
                                        <span className={styles['metric-value']}>
                                            {dashboardData.full_day + dashboardData.half_day + dashboardData.first_half + dashboardData.second_half}
                                        </span>
                                        <span className={styles['metric-label']}>Total Present</span>
                                    </div>
                                </div>
                                <div className={styles['metric-card']}>
                                    <div className={`${styles['metric-icon']} ${styles.warning}`}>
                                        <Clock size={24} />
                                    </div>
                                    <div className={styles['metric-info']}>
                                        <span className={styles['metric-value']}>{dashboardData.in_progress}</span>
                                        <span className={styles['metric-label']}>Currently Active</span>
                                    </div>
                                </div>
                                <div className={styles['metric-card']}>
                                    <div className={`${styles['metric-icon']} ${styles.danger}`}>
                                        <XCircle size={24} />
                                    </div>
                                    <div className={styles['metric-info']}>
                                        <span className={styles['metric-value']}>{dashboardData.absent}</span>
                                        <span className={styles['metric-label']}>Total Absent</span>
                                    </div>
                                </div>
                                <div className={styles['metric-card']}>
                                    <div className={`${styles['metric-icon']} ${styles.info}`}>
                                        <AlertCircle size={24} />
                                    </div>
                                    <div className={styles['metric-info']}>
                                        <span className={styles['metric-value']}>{dashboardData.pending_admin_approval}</span>
                                        <span className={styles['metric-label']}>Pending Approval</span>
                                    </div>
                                </div>
                                </div>
                            </div>

                            {/* Charts Grid */}
                            <div className={styles['charts-grid']}>
                                {/* Pie Chart */}
                                <div className={styles['chart-card']}>
                                    <div className={styles['chart-header']}>
                                        <PieChartIcon size={20} color="#f97316" />
                                        <h3>Attendance Distribution</h3>
                                    </div>
                                    <div className={styles['chart-container']}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <defs>
                                                    <filter id="shadow3d" x="-20%" y="-20%" width="140%" height="140%">
                                                        <feDropShadow dx="3" dy="6" stdDeviation="5" floodColor="#000" floodOpacity="0.25" />
                                                        <feDropShadow dx="-2" dy="-2" stdDeviation="4" floodColor="#fff" floodOpacity="0.5" />
                                                    </filter>
                                                </defs>
                                                <text x="50%" y="45%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '28px', fontWeight: '700', fill: '#0f172a', fontFamily: "'Outfit', sans-serif" }}>
                                                    {dashboardData?.total_employees || 0}
                                                </text>
                                                <text x="50%" y="58%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '13px', fill: '#64748b', fontFamily: "'Outfit', sans-serif" }}>
                                                    Total Employees
                                                </text>
                                                <Pie
                                                    data={getPieData()}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={75}
                                                    outerRadius={100}
                                                    paddingAngle={5}
                                                    dataKey="value"
                                                    animationDuration={1500}
                                                    animationBegin={200}
                                                    animationEasing="ease-out"
                                                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                                                    labelLine={false}
                                                    style={{ filter: 'url(#shadow3d)' }}
                                                >
                                                    {getPieData().map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                                                    ))}
                                                </Pie>
                                                <RechartsTooltip
                                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                                                    itemStyle={{ fontFamily: "'Outfit', sans-serif", fontWeight: '600' }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className={styles['legend-container']}>
                                        {getPieData().map((entry, index) => (
                                            <div key={index} className={styles['legend-item']}>
                                                <div className={styles['legend-color']} style={{ backgroundColor: entry.color }}></div>
                                                <span>{entry.name} ({entry.value})</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Bar Chart */}
                                <div className={styles['chart-card']}>
                                    <div className={styles['chart-header']}>
                                        <BarChart2 size={20} color="#3b82f6" />
                                        <h3>Status Breakdown</h3>
                                    </div>
                                    <div className={styles['chart-container']}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={getBarData()} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, fill: '#64748b' }} dy={10} />
                                                <YAxis axisLine={false} tickLine={false} tick={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, fill: '#64748b' }} />
                                                <RechartsTooltip
                                                    cursor={{ fill: '#f1f5f9' }}
                                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', fontFamily: "'Outfit', sans-serif" }}
                                                />
                                                <Bar dataKey="value" fill="#f97316" radius={[6, 6, 0, 0]} animationDuration={1000} barSize={40} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>

                            {/* Employee List Section */}
                            <div className={styles['employee-list-section']}>
                                <div className={styles['section-header']}>
                                    <div className={styles['section-title']}>
                                        <h2>Daily Attendance Records</h2>
                                        <p>List of all employee visit sessions for the selected date.</p>
                                    </div>
                                </div>

                                <div className={styles['data-view-container']}>
                                    <div className={styles['table-wrapper']}>
                                        <table className={styles['employee-table']}>
                                            <thead>
                                                <tr>
                                                    <th>EMPLOYEE & SITE</th>
                                                    <th>PUNCH TIMES</th>
                                                    <th>STATUS</th>
                                                    <th className={styles['actions-cell']}>ACTIONS</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {employeesList.length > 0 ? (
                                                    employeesList.map((record) => {
                                                        const empName = record.employee?.name || record.employee?.employee_name || 'N/A';
                                                        const projectName = record.project?.name || 'N/A';
                                                        const punchInTime = record.punch_in;
                                                        const punchOutTime = record.punch_out;
                                                        const statusVal = record?.attendance?.status || record?.session_status || 'ABSENT';

                                                        return (
                                                            <tr key={record.id}>
                                                                <td className={styles['name-cell']}>
                                                                    <div className={styles['user-info']}>
                                                                        <div
                                                                            className={styles['avatar']}
                                                                            style={{
                                                                                backgroundColor: getAvatarColor(empName),
                                                                                color: getAvatarTextColor(getAvatarColor(empName))
                                                                            }}
                                                                        >
                                                                            {getInitials(empName)}
                                                                        </div>
                                                                        <div className={styles['name-code-wrapper']}>
                                                                            <span className={styles['name-text']}>{empName}</span>
                                                                            <span style={{ fontSize: '12px', color: '#64748b' }}>{projectName}</span>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>
                                                                        <span style={{ color: '#16a34a' }}>In: {formatTime(punchInTime)}</span>
                                                                        <span style={{ color: '#dc2626' }}>Out: {formatTime(punchOutTime)}</span>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <span className={`${styles['status-pill']} ${getStatusClass(statusVal)}`}>
                                                                        {statusVal ? statusVal.replace(/_/g, ' ') : '-'}
                                                                    </span>
                                                                </td>
                                                                <td className={styles['actions-cell']}>
                                                                    <div className={`action-wrapper ${styles['action-wrapper']}`}>
                                                                        <button
                                                                            className={styles['action-btn']}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                toggleActionMenu(record.id, e);
                                                                            }}
                                                                        >
                                                                            <MoreVertical size={18} />
                                                                        </button>

                                                                        {openMenuIndex === record.id && (
                                                                            <div
                                                                                className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                                                                                style={{ top: menuPosition.top, left: menuPosition.left, position: 'fixed' }}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                            >
                                                                                <button
                                                                                    className={styles['dropdown-item']}
                                                                                    onClick={() => {
                                                                                        setOpenMenuIndex(null);
                                                                                        navigate(`/employees/attendance/view/${record.employee_id || record.employee?.id}`);
                                                                                    }}
                                                                                >
                                                                                    <Calendar size={16} /> View Attendance
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : !isEmpLoading && (
                                                    <NoData message="No records found." colSpan={4} isTable={true} />
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className={styles['desktop-pagination']}>
                                        <Pagination
                                            currentPage={empPagination.current_page}
                                            totalItems={empPagination.total}
                                            itemsPerPage={empPagination.per_page}
                                            onPageChange={(page) => setEmpPagination(prev => ({ ...prev, current_page: page }))}
                                        />
                                    </div>

                                    <div className={styles['mobile-pagination']}>
                                        <Pagination
                                            currentPage={empPagination.current_page}
                                            totalItems={empPagination.total}
                                            itemsPerPage={empPagination.per_page}
                                            onPageChange={(page) => setEmpPagination(prev => ({ ...prev, current_page: page }))}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    <Footer />
                </main>
            </div>
        </div>
    );
};

export default HRDashboard;
